import { getShopifyImageUrl } from "@/lib/shopify";
import { productImageAlt } from "@/lib/imageAlt";
import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Loader2, Download, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { getSupabase } from "@/integrations/supabase/lazy";
import { motion, AnimatePresence } from "framer-motion";

interface RoomVisualizerProps {
  productName: string;
  productImageUrl: string;
}

type Step = "upload" | "processing" | "result";

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve((event.target?.result as string) || "");
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });

const getExifOrientation = (buffer: ArrayBuffer) => {
  const view = new DataView(buffer);

  if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) {
    return 1;
  }

  let offset = 2;

  while (offset + 1 < view.byteLength) {
    const marker = view.getUint16(offset, false);
    offset += 2;

    if (marker === 0xffe1) {
      if (offset + 8 > view.byteLength) return 1;
      const segmentLength = view.getUint16(offset, false);
      const tiffOffset = offset + 8;

      if (segmentLength < 8 || view.getUint32(offset + 2, false) !== 0x45786966) {
        return 1;
      }

      const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
      const firstIfdOffset = view.getUint32(tiffOffset + 4, littleEndian);
      let directoryOffset = tiffOffset + firstIfdOffset;

      if (directoryOffset + 2 > view.byteLength) return 1;

      const entryCount = view.getUint16(directoryOffset, littleEndian);
      directoryOffset += 2;

      for (let index = 0; index < entryCount; index += 1) {
        const entryOffset = directoryOffset + index * 12;
        if (entryOffset + 10 > view.byteLength) break;

        if (view.getUint16(entryOffset, littleEndian) === 0x0112) {
          return view.getUint16(entryOffset + 8, littleEndian);
        }
      }

      return 1;
    }

    if ((marker & 0xff00) !== 0xff00 || offset + 2 > view.byteLength) {
      break;
    }

    const segmentLength = view.getUint16(offset, false);
    if (segmentLength < 2) break;
    offset += segmentLength;
  }

  return 1;
};

const applyOrientationTransform = (
  context: CanvasRenderingContext2D,
  orientation: number,
  width: number,
  height: number,
) => {
  switch (orientation) {
    case 2:
      context.transform(-1, 0, 0, 1, width, 0);
      break;
    case 3:
      context.transform(-1, 0, 0, -1, width, height);
      break;
    case 4:
      context.transform(1, 0, 0, -1, 0, height);
      break;
    case 5:
      context.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      context.transform(0, 1, -1, 0, height, 0);
      break;
    case 7:
      context.transform(0, -1, -1, 0, height, width);
      break;
    case 8:
      context.transform(0, -1, 1, 0, 0, width);
      break;
    default:
      break;
  }
};

const normalizeImageOrientation = async (file: File) => {
  const originalDataUrl = await readFileAsDataUrl(file);

  try {
    const orientation = getExifOrientation(await file.arrayBuffer());

    if (orientation === 1) {
      return originalDataUrl;
    }

    // Modern browsers auto-apply EXIF orientation when decoding images
    // (CSS `image-orientation: from-image` is the default). Use createImageBitmap
    // with `imageOrientation: 'from-image'` to get an already-correctly-oriented
    // bitmap, then bake it into a canvas — no manual transform needed (which
    // would double-rotate).
    if (typeof createImageBitmap === "function") {
      try {
        const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
        const canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const context = canvas.getContext("2d");
        if (!context) {
          bitmap.close?.();
          return originalDataUrl;
        }
        context.drawImage(bitmap, 0, 0);
        bitmap.close?.();
        return canvas.toDataURL(file.type === "image/png" ? "image/png" : "image/jpeg", 0.92);
      } catch (bitmapError) {
        console.warn("createImageBitmap failed, falling back to manual rotation:", bitmapError);
      }
    }

    // Fallback for browsers without createImageBitmap orientation support:
    // manually apply the EXIF transform.
    const objectUrl = URL.createObjectURL(file);

    try {
      const image = new Image();
      image.src = objectUrl;
      await image.decode();

      const width = image.naturalWidth;
      const height = image.naturalHeight;
      const canvas = document.createElement("canvas");
      const rotated = [5, 6, 7, 8].includes(orientation);

      canvas.width = rotated ? height : width;
      canvas.height = rotated ? width : height;

      const context = canvas.getContext("2d");
      if (!context) return originalDataUrl;

      applyOrientationTransform(context, orientation, width, height);
      context.drawImage(image, 0, 0, width, height);

      return canvas.toDataURL(file.type === "image/png" ? "image/png" : "image/jpeg", 0.92);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    console.error("Failed to normalize image orientation:", error);
    return originalDataUrl;
  }
};

export const RoomVisualizer = ({ productName, productImageUrl }: RoomVisualizerProps) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("upload");
  const [roomPreview, setRoomPreview] = useState<string | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep("upload");
    setRoomPreview(null);
    setResultImage(null);
  };

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }

    const normalizedPreview = await normalizeImageOrientation(file);
    setRoomPreview(normalizedPreview);
  }, []);

  const handleVisualize = async () => {
    if (!roomPreview) return;
    setStep("processing");

    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke("room-visualizer", {
        body: {
          roomImageBase64: roomPreview,
          productImageUrl,
          productName,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.imageUrl) throw new Error("No image generated");

      setResultImage(data.imageUrl);
      setStep("result");
    } catch (err: any) {
      console.error("Visualization error:", err);
      toast.error(err.message || "Failed to generate visualization. Please try again.");
      setStep("upload");
    }
  };

  const handleDownload = () => {
    if (!resultImage) return;
    const link = document.createElement("a");
    link.href = resultImage;
    link.download = `room-visualizer-${productName.replace(/\s+/g, "-").toLowerCase()}.png`;
    link.click();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full rounded-full h-[44px] text-[12px] tracking-[0.12em] uppercase font-bold gap-2 border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50 transition-all"
        >
          <Camera className="h-4 w-4" />
          See It In Your Space
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {step === "result" ? "Your Room, Reimagined" : "See It In Your Space"}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {step === "upload" && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <p className="text-sm text-muted-foreground">
                Upload a photo of your room and our AI will show you how{" "}
                <span className="font-medium text-foreground">{productName}</span> would look in your space.
              </p>

              {/* Product preview */}
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                <img
                  src={getShopifyImageUrl(productImageUrl, 160)}
                  alt={productImageAlt(productName)}
                  className="w-14 h-14 object-contain rounded bg-background"
                  loading="lazy"
                  decoding="async"
                />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Visualizing</p>
                  <p className="text-sm font-medium truncate">{productName}</p>
                </div>
              </div>

              {/* Upload area */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />

              {roomPreview ? (
                <div className="relative">
                  <img
                    src={roomPreview}
                    alt="Your room"
                    className="w-full rounded-lg border border-border object-cover max-h-[300px]"
                  />
                  <button
                    onClick={() => { setRoomPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    className="absolute top-2 right-2 bg-background/80 rounded-full p-1.5 hover:bg-background transition-colors"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-border rounded-lg p-8 flex flex-col items-center gap-3 hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer"
                >
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <div className="text-center">
                    <p className="text-sm font-medium">Upload a room photo</p>
                    <p className="text-xs text-muted-foreground mt-1">JPG, PNG up to 10MB</p>
                  </div>
                </button>
              )}

              <Button
                onClick={handleVisualize}
                disabled={!roomPreview}
                className="w-full rounded-full h-[48px] text-[12px] tracking-[0.12em] uppercase font-bold gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Visualize In My Room
              </Button>

              <p className="text-[10px] text-muted-foreground text-center">
                AI-generated visualization for reference only. Actual product may vary in color and scale.
              </p>
            </motion.div>
          )}

          {step === "processing" && (
            <motion.div
              key="processing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col items-center gap-4 py-12"
            >
              <div className="relative">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <Sparkles className="h-4 w-4 text-primary absolute -top-1 -right-1 animate-pulse" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">Creating your visualization…</p>
                <p className="text-xs text-muted-foreground mt-1">This usually takes 15-30 seconds</p>
              </div>
            </motion.div>
          )}

          {step === "result" && resultImage && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <img
                src={resultImage}
                alt="Room visualization"
                className="w-full rounded-lg border border-border"
              />

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDownload}
                  className="flex-1 rounded-full h-[44px] text-[11px] tracking-[0.1em] uppercase font-bold gap-2"
                >
                  <Download className="h-4 w-4" />
                  Save Image
                </Button>
                <Button
                  onClick={reset}
                  className="flex-1 rounded-full h-[44px] text-[11px] tracking-[0.1em] uppercase font-bold gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Try Another Room
                </Button>
              </div>

              <p className="text-[10px] text-muted-foreground text-center">
                AI-generated visualization for reference only. Actual product may vary.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
